import { isFinalizedAttendance } from "../../../LambdaFunctions/shared/lessonAttendance";
import { displayDate } from "../common/datePresentation";
import API from "../../services/SchoolClassService";
import { WEEKDAYS } from "../common/Const";
import { minutesToTimeString, timeStringToMinutes } from "../common/Helpers";
import {
  DEFAULT_LESSON_TYPES_SETTING,
  getLessonTypeById,
  getLessonTypeDisplayName,
  normalizeLessonTypesSetting,
} from "../common/lessonTypes";

export const getLessonIndexFromId = (lessonId) => {
  const value = String(lessonId || "");
  return value.includes("-") ? value.substring(value.lastIndexOf("-") + 1) : "";
};

export const getClassIdFromLessonId = (lessonId) => {
  const value = String(lessonId || "");
  return value.includes("-") ? value.substring(0, value.lastIndexOf("-")) : value;
};

export const buildAttendanceRoute = (classId, lessonIndex, search = "") => ({
  pathname: `/manage/class/${classId}/lesson/${lessonIndex}/attendance`,
  search,
});

const getName = person => (
  person ? `${person.firstName || ""} ${person.lastName || ""}`.trim() : ""
);

const getLessonEndTime = (lessonDetails, lessonTypes) => {
  const classType = getLessonTypeById(lessonTypes, lessonDetails.classTypeId);
  const startTimeMinutes = timeStringToMinutes(lessonDetails.startTime || "");

  if (!classType.durationMinutes || startTimeMinutes === null) {
    return "";
  }

  return minutesToTimeString(startTimeMinutes + classType.durationMinutes);
};

const getWeekdayName = (dayOfWeek) => {
  const weekday = WEEKDAYS.find(item => item.id === dayOfWeek);
  return weekday ? weekday.name : dayOfWeek || "";
};

const getLessonDetails = scheduledLesson => (
  scheduledLesson.lessonRegister
    ? {
      ...(scheduledLesson.defaultLessonDetails || {}),
      ...(scheduledLesson.lessonRegister.lessonDetails || {}),
    }
    : scheduledLesson.defaultLessonDetails
);

const getLessonParticipants = (scheduledLesson, students) => {
  if (isFinalizedAttendance(scheduledLesson.lessonRegister)) {
    return scheduledLesson.lessonRegister.students.map(participant => (
      (students).find(student => student.id === participant.id) || participant
    ));
  }
  const defaultStudents = scheduledLesson.defaultStudents || [];
  const defaultStudentIds = defaultStudents.map(student => student.id);
  const registerStudents = (scheduledLesson.lessonRegister && scheduledLesson.lessonRegister.students) || [];
  const registerOnlyStudents = registerStudents
    .filter(student => !defaultStudentIds.includes(student.id))
    .map(registerStudent => (
      students.find(student => student.id === registerStudent.id) || registerStudent
    ));

  return [
    ...defaultStudents,
    ...registerOnlyStudents,
  ];
};

export const getAttendanceLessonViewModel = ({
  schoolClasses,
  teachers,
  students,
  classStudents,
  lessonRegister,
  books,
  classId,
  lessonIndex,
  lessonTypes = DEFAULT_LESSON_TYPES_SETTING,
}) => {
  const rosterStudents = [...students];
  classStudents.filter(item => item.schoolClassId === classId && item.studentName).forEach(item => {
    if (!rosterStudents.some(student => student.id === item.studentId)) rosterStudents.push({ id: item.studentId, ...item.studentName });
  });
  const normalizedLessonTypes = normalizeLessonTypesSetting(lessonTypes);
  const schoolClass = schoolClasses.find(item => item.id === classId);
  if (!schoolClass) {
    return { status: "missing-class" };
  }

  const scheduledLessons = API.generateScheduledClasses(
    schoolClass,
    lessonRegister,
    rosterStudents,
    classStudents,
  );
  const scheduledLesson = scheduledLessons[Number(lessonIndex)];

  if (!scheduledLesson) {
    return { status: "missing-lesson", schoolClass };
  }

  const lessonDetails = getLessonDetails(scheduledLesson);
  const participants = getLessonParticipants(scheduledLesson, rosterStudents);
  const scheduledLessonWithParticipants = {
    ...scheduledLesson,
    defaultStudents: participants,
  };
  const teacher = teachers.find(item => item.id === lessonDetails.teacherId);
  const book = books.find(item => item.id === schoolClass.bookId);
  const classTypeName = getLessonTypeDisplayName(lessonDetails.classTypeId, normalizedLessonTypes);
  const endTime = getLessonEndTime(lessonDetails, normalizedLessonTypes);
  const weekdayName = getWeekdayName(lessonDetails.dayOfWeek);

  return {
    status: "ready",
    schoolClass,
    scheduledLesson: scheduledLessonWithParticipants,
    lessonDetails,
    endTime,
    teacherName: getName(teacher),
    classTypeName,
    weekdayName,
    bookName: book ? book.title || book.name || "" : "",
    participants,
    title: `${weekdayName}, ${displayDate(lessonDetails.date)} - ${classTypeName}`,
  };
};
